import { createAvatar } from '@dicebear/core';
import { bottts } from '@dicebear/collection';
import { getPreSignedUrlForUpload } from 'lib/api/services/storage.service';
import { getFile } from 'services/storage-service';
import { agentService } from 'features/chat/agent-chat-module/services/agent.service';
import type {
  Agent,
  AgentFormality,
  AgentResponseStyle,
  AgentTechnicalLevel,
  IUpdateAgentPersonaPayload,
} from 'features/chat/agent-chat-module/types/agent.service.type';
import type { ConfigurationSettings } from 'models/configuration-settings';
import type { QueryClient } from '@tanstack/react-query';

export interface GenerateBotttsAvatarResult {
  file: File;
  seed: string;
  svgText: string;
}

export const generateBotttsAvatar = (name?: string, description?: string): GenerateBotttsAvatarResult => {
  const randomPart = Math.random().toString(36).substring(2, 8);
  const agentInfo = [name?.trim() || 'Agent', description?.trim()].filter(Boolean).join('-');
  const seed = `${agentInfo}-${randomPart}`;

  const avatar = createAvatar(bottts, {
    seed,
    scale: 80
  });

  const svgText = avatar.toString();
  const blob = new Blob([svgText], { type: 'image/svg+xml' });
  const file = new File([blob], `dicebear-${seed}.svg`, { type: 'image/svg+xml' });

  return { file, seed, svgText };
};

export interface GenerateAndSaveAgentAvatarParams {
  agentId: string;
  name: string;
  description?: string;
  projectKey: string;
  configurationSettings: ConfigurationSettings;
  agent?: Agent;
  queryClient?: QueryClient;
}

export const generateAndSaveAgentAvatar = async ({
  agentId,
  name,
  description,
  projectKey,
  configurationSettings,
  agent,
  queryClient,
}: GenerateAndSaveAgentAvatarParams): Promise<{ logoId: string; logoUrl: string }> => {
  const { file } = generateBotttsAvatar(name, description);

  const presign = await getPreSignedUrlForUpload(
    {
      name: file.name,
      projectKey,
      metaData: '',
      accessModifier: 'Public',
      configurationName: 'Default',
      parentDirectoryId: '',
      tags: '',
    },
    projectKey
  );

  if (!presign.isSuccess || !presign.uploadUrl || !presign.fileId) {
    throw new Error(`Failed to get upload URL for ${file.name}`);
  }

  const uploadResponse = await fetch(presign.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
      'x-ms-blob-type': 'BlockBlob',
    },
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed for ${file.name}`);
  }

  const fileInfo = await getFile(presign.fileId, configurationSettings);
  const logoUrl =
    fileInfo?.isSuccess && fileInfo?.url ? fileInfo.url : presign.uploadUrl.split('?')[0];

  let currentAgent = agent;
  if (!currentAgent) {
    try {
      const fetched = await agentService.getAgentById({ id: agentId, projectKey }, projectKey);
      currentAgent = fetched?.agent;
    } catch {
      // If fetching fails, proceed with default persona values
    }
  }

  const payload: IUpdateAgentPersonaPayload = {
    agent_id: agentId,
    agent_type: currentAgent?.agent_type || '',
    name: name.trim(),
    language: currentAgent?.language || '',
    tags: currentAgent?.tags ?? [],
    domain_expertise: currentAgent?.domain_expertise ?? [],
    role: currentAgent?.role ?? '',
    description: description?.trim() || '',
    logo_id: presign.fileId,
    logo_url: logoUrl,
    response_style: (currentAgent?.response_style || 'Direct') as AgentResponseStyle,
    formality_level: (currentAgent?.formality_level || 'Neutral') as AgentFormality,
    technical_level: (currentAgent?.technical_level || 'General') as AgentTechnicalLevel,
    project_key: projectKey,
    is_any_agent: currentAgent?.is_any_agent ?? false,
    visibility: currentAgent?.visibility,
  };

  await agentService.updateAgentPersona(payload, projectKey);

  if (currentAgent?.widget) {
    try {
      await agentService.updateAgentWidget(
        {
          agent_id: agentId,
          project_key: projectKey,
          regen_widget_id: false,
          widget: {
            ...currentAgent.widget,
            logo_id: presign.fileId,
            logo_url: logoUrl,
          },
        },
        projectKey
      );
    } catch {
      // Non-blocking widget update error
    }
  }

  if (queryClient) {
    queryClient.invalidateQueries({ queryKey: ['agent'] });
    queryClient.invalidateQueries({ queryKey: ['agents'] });
  }

  return { logoId: presign.fileId, logoUrl };
};
